import { useCallback } from "react";
import { hasPermission, type PermissionModule } from "@/shared/auth/permissions";
import { useSession } from "@/shared/auth/session";

/**
 * `can("devices", "manage")` — the UI hides what the role lacks; the API enforces it too (rules/01 §10).
 *
 * `deviceScope` is the other half of the same question (M8 §3, ADR-0006 §3): a role limited to a set
 * of terminals sees a smaller organisation rather than a filtered screen, so every list already
 * arrives filtered and nothing here re-filters. What it does change is which affordances make sense:
 * pairing, unpairing, the unclaimed lookup and role editing are organisation-wide and answer
 * 409 `SCOPE_LIMITED`, so they are hidden rather than offered and then refused.
 */
export function usePermissions() {
  const { permissions, role } = useSession();
  const can = useCallback(
    (module: PermissionModule, action: string) => hasPermission(permissions, module, action),
    [permissions],
  );
  const deviceScope = role.deviceScope;
  return {
    can,
    /** The terminals this role may see, or null for every terminal in the organisation. */
    deviceScope,
    scoped: deviceScope !== null,
    scopedCount: deviceScope?.length ?? 0,
  };
}
